import { useTranslation } from "react-i18next";
import { ArrowDownUp } from "lucide-react";

import { WFPButton } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface SortOption {
  id: string;
  translationKey: string;
}

interface SortMenuProps {
  className?: string;
  onSortColumnChange: (sortColumn: string) => void;
  onSortDirectionChange: (sortDirection: string) => void;
  options: readonly SortOption[];
  sortColumn: string;
  sortDirection: string;
}

const MENU_ITEM_CLASS =
  "data-[state=checked]:bg-primary-50 data-[state=checked]:font-medium data-[state=checked]:text-primary-700 focus:bg-primary-100 focus:text-primary-700";

export const SortMenu = ({
  className,
  onSortColumnChange,
  onSortDirectionChange,
  options,
  sortColumn,
  sortDirection,
}: SortMenuProps) => {
  const { t } = useTranslation();
  const selectedOption = options.find(option => option.id === sortColumn);
  const buttonLabel = selectedOption
    ? t(selectedOption.translationKey)
    : t("common.sorting.button");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <WFPButton type="button" variant="outline" className={className}>
          <ArrowDownUp className="size-4" aria-hidden="true" />
          {buttonLabel}
        </WFPButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>{t("common.sorting.sortBy")}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={sortColumn} onValueChange={onSortColumnChange}>
          {options.map(option => (
            <DropdownMenuRadioItem
              key={option.id}
              value={option.id}
              indicator="check"
              className={MENU_ITEM_CLASS}
            >
              {t(option.translationKey)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t("common.sorting.order")}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={sortDirection} onValueChange={onSortDirectionChange}>
          <DropdownMenuRadioItem value="asc" indicator="check" className={MENU_ITEM_CLASS}>
            {t("common.sorting.ascending")}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="desc" indicator="check" className={MENU_ITEM_CLASS}>
            {t("common.sorting.descending")}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
